import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { contractsApi, customersApi, templatesApi, productsApi } from '../lib/api';
import { formatDate, STATUS_LABELS, getStatusColor, CONTRACT_TYPE_LABELS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Plus,
    MagnifyingGlass,
    FileText,
    Funnel,
    Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import CreateContractDialog from '../components/CreateContractDialog';

export default function ContractsPage() {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const contractsRes = await contractsApi.getAll();
            setContracts(contractsRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateContract = async (payload) => {
        const response = await contractsApi.create(payload);
        toast.success('Contratto creato con successo');
        navigate(`/contratti/${response.data.id}`);
    };

    const filteredContracts = contracts.filter(c => {
        const matchesSearch = !search || 
            c.numero_contratto?.toLowerCase().includes(search.toLowerCase()) ||
            c.product_name?.toLowerCase().includes(search.toLowerCase()) ||
            c.customer_name?.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="contracts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex flex-1 gap-3">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca contratti..."
                            data-testid="contracts-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-48" data-testid="status-filter">
                            <Funnel className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Stato" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti gli stati</SelectItem>
                            <SelectItem value="in_corso">In corso</SelectItem>
                            <SelectItem value="completato">Completato</SelectItem>
                            <SelectItem value="bloccato">Bloccato</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button data-testid="create-contract-button" onClick={() => setDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Contratto
                </Button>
                <CreateContractDialog 
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    onSuccess={handleCreateContract}
                />
            </div>

            {/* Contracts Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-header">
                                <TableHead>Contratto</TableHead>
                                <TableHead>Cliente</TableHead>
                                <TableHead>Tipologia</TableHead>
                                <TableHead>Stato</TableHead>
                                <TableHead>Progresso</TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredContracts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12">
                                        <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Nessun contratto trovato</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredContracts.map((contract) => (
                                    <TableRow 
                                        key={contract.id} 
                                        className="table-row cursor-pointer"
                                        onClick={() => navigate(`/contratti/${contract.id}`)}
                                        data-testid={`contract-row-${contract.id}`}
                                    >
                                        <TableCell className="table-cell">
                                            <div>
                                                <p className="font-medium">{contract.numero_contratto}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(contract.created_at)}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {contract.customer_name || '-'}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Badge variant="outline">
                                                {contract.product_name || '-'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Badge className={getStatusColor(contract.status)}>
                                                {STATUS_LABELS[contract.status] || contract.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <div className="w-24">
                                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                                    <span>{contract.completed_steps}/{contract.total_steps}</span>
                                                    <span>{Math.round(contract.progress || 0)}%</span>
                                                </div>
                                                <Progress value={contract.progress || 0} className="h-1.5" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell text-muted-foreground">
                                            {formatDate(contract.data_contratto)}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Button variant="ghost" size="sm">
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
